import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { AdminSupportTab } from "@/components/AdminSupportTab";
import { AdminIntegrationsTab } from "@/components/AdminIntegrationsTab";
import { AdminPrivacyTab } from "@/components/AdminPrivacyTab";
import { RevenueChart } from "@/components/RevenueChart";
import { BulkAttachDialog } from "@/components/BulkAttachDialog";
import { ExtractionHealthCard } from "@/components/ExtractionHealthCard";
import { downloadOrderDocument, downloadFinishedReport, viewOrderDocument, viewFinishedReport } from "@/components/OrdersTable";
import { AdminOrderDetailsDialog, MemberDetailsDialog } from "@/components/AdminOrderDetails";
import { api, apiErrorMessage, centsToUsd } from "@/lib/api";
import {
  Users,
  ShieldCheck,
  FileWarning,
  FileCheck2,
  CircleDollarSign,
  Send,
  Download,
  RefreshCw,
  Loader2,
  Paperclip,
  RotateCcw,
  Layers,
  Eye,
} from "lucide-react";

const ADMIN_STATUSES = ["new", "paid", "processing", "on_hold", "completed", "cancelled", "refunded"];

export default function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminUnread, setAdminUnread] = useState(0);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [attaching, setAttaching] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const reportInputRef = useRef(null);

  // Google OAuth redirect feedback
  useEffect(() => {
    const google = searchParams.get("google");
    if (google) {
      if (google === "connected") {
        toast.success("Google connected! Drive folders, sheets, and receipts will now sync automatically.");
      } else if (google === "missing_scopes") {
        toast.error(
          "Google connection incomplete — please approve BOTH the Drive and Sheets permissions (tick both checkboxes).",
          { duration: 12000 }
        );
      } else if (google === "state_mismatch") {
        toast.error(
          "Google connection failed a security check — this usually means the sign-in tab was stale. Please click Connect Google again and complete it in one go.",
          { duration: 12000 }
        );
      } else {
        const reason = searchParams.get("reason");
        toast.error(
          `Google connection failed${reason ? ` (reason: ${reason})` : ""}. Please try Connect Google again.`,
          { duration: 12000 }
        );
      }
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      const { data } = await api.post(`/admin/orders/${refundTarget.id}/refund`);
      toast.success(data.message || "Refund processed.");
      setRefundTarget(null);
      loadAll();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Refund failed."), { duration: 8000 });
    } finally {
      setRefunding(false);
    }
  };

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets/unread");
      setAdminUnread(data.adminUnread || 0);
    } catch (error) {
      // Non-critical badge refresh; log so failures are still visible in devtools.
      console.warn("Unread badge refresh failed:", error?.message || error);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [statsRes, ordersRes, usersRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/users"),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
      setUsers(usersRes.data.users);
      loadUnread();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load admin data."));
    } finally {
      setRefreshing(false);
    }
  }, [loadUnread]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { adminStatus: status });
      toast.success(`Order ${orderId.slice(0, 8)} set to ${status.replace("_", " ")}.`);
      loadAll();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Status update failed."));
    }
  };

  const testTelegram = async () => {
    setTestingTelegram(true);
    try {
      await api.post("/telegram/test");
      toast.success("Telegram test message sent.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Telegram test failed."));
    } finally {
      setTestingTelegram(false);
    }
  };

  const startAttachReport = (orderId) => {
    setAttachTarget(orderId);
    reportInputRef.current?.click();
  };

  const handleReportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !attachTarget) return;
    setAttaching(attachTarget);
    const body = new FormData();
    body.append("report", file);
    try {
      await api.post(`/admin/orders/${attachTarget}/report`, body);
      toast.success("Finished report attached. The member has been notified and emailed.");
      loadAll();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Report upload failed."));
    } finally {
      setAttaching("");
      setAttachTarget(null);
    }
  };

  const statCards = stats
    ? [
        { label: "Registered users", value: stats.users, icon: Users },
        { label: "Active members", value: stats.activeMembers, icon: ShieldCheck },
        { label: "Pending verifications", value: stats.pendingVerifications, icon: FileWarning },
        { label: "Paid reports", value: stats.paidReports, icon: FileCheck2 },
        { label: "Revenue", value: centsToUsd(stats.revenueCents), icon: CircleDollarSign },
      ]
    : [];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <input
        ref={reportInputRef}
        type="file"
        accept=".pdf,.docx,.png,.jpg,.jpeg"
        className="hidden"
        data-testid="admin-report-file-input"
        onChange={handleReportFile}
      />
      <main className="flex-1 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Operations dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor members, verification exceptions, payments, and fulfillment status.
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <Button variant="outline" size="sm" onClick={loadAll} disabled={refreshing} data-testid="admin-refresh-button">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" onClick={testTelegram} disabled={testingTelegram} data-testid="telegram-test-button">
              {testingTelegram ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Test Telegram</>}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
          {statCards.map((card, index) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.05 }}
            >
              <Card className="bg-card border-border" data-testid={`admin-stat-${index}`}>
                <CardContent className="p-5 space-y-2">
                  <card.icon className="h-4.5 w-4.5 h-5 w-5 text-primary" />
                  <p className="font-display text-2xl font-semibold">{card.value}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">{card.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Revenue & orders chart (last 12 months) */}
        <RevenueChart />

        <Tabs defaultValue="orders">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="orders" data-testid="admin-orders-tab">Operations queue</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-users-tab">Users</TabsTrigger>
            <TabsTrigger value="support" data-testid="admin-support-tab">
              Support
              {adminUnread > 0 && (
                <span
                  data-testid="admin-support-tab-unread-badge"
                  className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
                >
                  {adminUnread > 99 ? "99+" : adminUnread}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="privacy" data-testid="admin-privacy-tab">Privacy</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="admin-integrations-tab">Integrations</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="pt-4 space-y-4">
            <ExtractionHealthCard />
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="font-display text-lg">All report requests</CardTitle>
                    <CardDescription>Change operational status through the allowlisted states.</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="bulk-attach-open-button"
                    onClick={() => setBulkOpen(true)}
                  >
                    <Layers className="h-4 w-4 mr-1.5" /> Bulk attach reports
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6" data-testid="admin-orders-empty">
                    No report requests submitted yet.
                  </p>
                ) : (
                  <ScrollArea className="w-full">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs uppercase tracking-wide">Order</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide">Member</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide">Type</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide">Purchase</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide">Identifier</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide">Verification</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide">Payment</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide">Ops status</TableHead>
                          <TableHead className="text-xs uppercase tracking-wide text-right">Doc / Report</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow key={order.id} className="hover:bg-secondary/40" data-testid={`admin-order-row-${order.id}`}>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              <button
                                type="button"
                                className="underline decoration-dotted underline-offset-2 hover:text-primary transition-colors"
                                data-testid={`admin-order-details-btn-${order.id}`}
                                onClick={() => setDetailsOrder(order)}
                              >
                                {order.id.slice(0, 8)}
                              </button>
                            </TableCell>
                            <TableCell className="text-sm">{order.user_email}</TableCell>
                            <TableCell className="text-sm capitalize">{order.request_type.replaceAll("_", " ")}</TableCell>
                            <TableCell>
                              <span
                                className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                                  order.purchase_type === "single"
                                    ? "border-primary/30 bg-primary/10 text-primary"
                                    : "border-border bg-secondary text-muted-foreground"
                                }`}
                              >
                                {order.purchase_type === "single" ? "Single" : "Member"}
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{order.identifier_value}</TableCell>
                            <TableCell><StatusBadge status={order.verification_status} /></TableCell>
                            <TableCell><StatusBadge status={order.report_paid ? "paid" : "unpaid"} /></TableCell>
                            <TableCell>
                              <Select value={order.admin_status} onValueChange={(value) => updateStatus(order.id, value)}>
                                <SelectTrigger className="h-8 w-36" data-testid={`admin-status-select-${order.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ADMIN_STATUSES.map((status) => (
                                    <SelectItem key={status} value={status}>
                                      {status.replace("_", " ")}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  aria-label="View member's uploaded document"
                                  data-testid={`admin-view-doc-${order.id}`}
                                  onClick={() => viewOrderDocument(order)}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" /> View doc
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  aria-label="Download document"
                                  data-testid={`admin-download-${order.id}`}
                                  onClick={() => downloadOrderDocument(order)}
                                >
                                  <Download className="h-3.5 w-3.5" />
                                </Button>
                                {order.report_filename && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-[hsl(var(--status-verified)/0.4)] text-[hsl(var(--status-verified))] hover:bg-[hsl(var(--status-verified)/0.1)]"
                                      aria-label="View attached report"
                                      data-testid={`admin-view-report-${order.id}`}
                                      onClick={() => viewFinishedReport(order)}
                                    >
                                      <Eye className="h-3.5 w-3.5 mr-1" /> Report
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-[hsl(var(--status-verified))]"
                                      aria-label="Download finished report"
                                      data-testid={`admin-report-download-${order.id}`}
                                      onClick={() => downloadFinishedReport(order)}
                                    >
                                      <FileCheck2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                                {order.report_paid && order.admin_status !== "refunded" && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-destructive hover:text-destructive"
                                    aria-label="Refund this order"
                                    data-testid={`admin-refund-${order.id}`}
                                    onClick={() => setRefundTarget(order)}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={attaching === order.id}
                                  data-testid={`admin-attach-report-${order.id}`}
                                  onClick={() => startAttachReport(order.id)}
                                >
                                  {attaching === order.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <>
                                      <Paperclip className="h-3.5 w-3.5 mr-1" />
                                      {order.report_filename ? "Replace" : "Attach report"}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="pt-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-lg">Users &amp; memberships</CardTitle>
                <CardDescription>Every account with its membership tier and status.</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-wide">Email</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Role</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Tier</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Interval</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Membership</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((row) => (
                        <TableRow
                          key={row.id}
                          className="hover:bg-secondary/40 cursor-pointer"
                          data-testid={`admin-user-row-${row.id}`}
                          onClick={() => setMemberDetails(row)}
                        >
                          <TableCell className="text-sm underline decoration-dotted underline-offset-2">{row.email}</TableCell>
                          <TableCell className="text-sm capitalize">{row.role}</TableCell>
                          <TableCell className="text-sm capitalize">{row.tier || "\u2014"}</TableCell>
                          <TableCell className="text-sm capitalize">{row.billing_interval || "\u2014"}</TableCell>
                          <TableCell>
                            {row.membership_status ? (
                              <StatusBadge status={row.membership_status === "active" ? "verified" : "cancelled"} />
                            ) : (
                              <span className="text-xs text-muted-foreground">None</span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : "\u2014"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="support" className="pt-4">
            <AdminSupportTab onUnreadChange={loadUnread} />
          </TabsContent>

          <TabsContent value="privacy" className="pt-4">
            <AdminPrivacyTab />
          </TabsContent>

          <TabsContent value="integrations" className="pt-4">
            <AdminIntegrationsTab />
          </TabsContent>
        </Tabs>
      </main>

      <BulkAttachDialog open={bulkOpen} onOpenChange={setBulkOpen} orders={orders} onDone={loadAll} />

      <AdminOrderDetailsDialog
        order={detailsOrder}
        open={Boolean(detailsOrder)}
        onOpenChange={(open) => !open && setDetailsOrder(null)}
      />
      <MemberDetailsDialog
        member={memberDetails}
        orders={orders}
        open={Boolean(memberDetails)}
        onOpenChange={(open) => !open && setMemberDetails(null)}
        onOpenOrder={(order) => {
          setMemberDetails(null);
          setDetailsOrder(order);
        }}
      />

      <AlertDialog open={Boolean(refundTarget)} onOpenChange={(open) => !open && setRefundTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Refund this order?</AlertDialogTitle>
            <AlertDialogDescription>
              {refundTarget && (
                <>
                  Order <span className="font-mono">{refundTarget.id?.slice(0, 8)}</span> for{" "}
                  {refundTarget.user_email}. If it was paid through Stripe, the card will be refunded
                  immediately; otherwise it is marked refunded in the records. The member is notified by
                  email. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="refund-cancel-button">Keep payment</AlertDialogCancel>
            <AlertDialogAction
              data-testid="refund-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={refunding}
              onClick={(event) => {
                event.preventDefault();
                confirmRefund();
              }}
            >
              {refunding ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}
              Issue refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SiteFooter />
    </div>
  );
}
